import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, X } from "lucide-react";
import { api } from "@/lib/api";

const REF_KEY = "bayo_ref_code";
const REF_TS_KEY = "bayo_ref_ts";
const TTL_DAYS = 30;
const DISMISSED_KEY = "bayo_ref_banner_dismissed";

/** Reads ?ref=CODE on first render, persists it, validates with the server,
 * and shows a discreet sticky banner. Stays for 30 days. */
export default function ReferralBanner() {
  const [info, setInfo] = useState(null); // { code, referrer_name }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlRef = (params.get("ref") || "").trim().toUpperCase();
    if (urlRef) {
      localStorage.setItem(REF_KEY, urlRef);
      localStorage.setItem(REF_TS_KEY, String(Date.now()));
      // Clean the URL so it doesn't bounce on every navigation
      params.delete("ref");
      const search = params.toString();
      const newUrl = window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }

    const stored = localStorage.getItem(REF_KEY);
    const ts = parseInt(localStorage.getItem(REF_TS_KEY) || "0", 10);
    const ageDays = ts ? (Date.now() - ts) / 86400000 : Infinity;
    if (!stored || ageDays > TTL_DAYS) {
      localStorage.removeItem(REF_KEY);
      localStorage.removeItem(REF_TS_KEY);
      return;
    }
    if (sessionStorage.getItem(DISMISSED_KEY) === stored) {
      setDismissed(true);
      return;
    }
    api
      .validateReferral(stored)
      .then((res) => {
        if (res.valid) setInfo({ code: stored, referrer_name: res.referrer_name });
      })
      .catch(() => {});
  }, []);

  if (!info || dismissed) return null;

  const close = () => {
    sessionStorage.setItem(DISMISSED_KEY, info.code);
    setDismissed(true);
  };

  return (
    <div
      data-testid="referral-banner"
      className="bg-[color:var(--navy)] text-white text-xs sm:text-sm px-4 py-3 flex items-center justify-center gap-3 sm:gap-6 flex-wrap"
    >
      <Gift size={16} strokeWidth={1.5} />
      <p className="text-center leading-snug">
        <span className="font-serif-display text-base sm:text-lg mr-2">-10 %</span>
        sur votre première commande grâce à <strong>{info.referrer_name}</strong> · code{" "}
        <span data-testid="referral-banner-code" className="font-mono tracking-wider">{info.code}</span>
      </p>
      <Link
        to="/shop"
        data-testid="referral-banner-cta"
        className="underline underline-offset-4 decoration-[color:var(--red)] hover:decoration-white"
      >
        Voir la boutique →
      </Link>
      <button onClick={close} data-testid="referral-banner-close" aria-label="Fermer" className="opacity-70 hover:opacity-100">
        <X size={14} />
      </button>
    </div>
  );
}

export function getStoredReferralCode() {
  if (typeof window === "undefined") return null;
  const code = localStorage.getItem(REF_KEY);
  const ts = parseInt(localStorage.getItem(REF_TS_KEY) || "0", 10);
  const ageDays = ts ? (Date.now() - ts) / 86400000 : Infinity;
  if (!code || ageDays > TTL_DAYS) return null;
  return code;
}

export function clearStoredReferralCode() {
  localStorage.removeItem(REF_KEY);
  localStorage.removeItem(REF_TS_KEY);
}
